if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        this.maxQuantityAttr = this.getAttribute('data-max-quantity');
        if (parseInt(this.maxQuantityAttr) != 0) {
          this.maxQuantity = parseInt(this.maxQuantityAttr) || 0;
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        if (!(await this.validateServiceSchedule(formData, config))) {
          return;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                quantityAdded: formData.get('quantity'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
              this.resetQuantityToDefault();
            }
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      async validateServiceSchedule(formData, config) {
        try {
          // Early exit if service schedule is not properly configured
          if (!window.nova || !window.nova.service_schedule) {
            throw new Error('Service schedule configuration is missing');
          }

          const schedule = window.nova.service_schedule;
          const currentDate = new Date();
          
          // Helper function to safely parse dates
          const formatScheduleDate = (dateString) => {
            if (!dateString) throw new Error('Invalid date string');
            return new Date(dateString.replace('///', ' '));
          };

          const initialDate = formatScheduleDate(schedule.initial_date);
          const finalDate = formatScheduleDate(schedule.final_date);

          // Get product-specific quantity limit from data attribute
          const productQuantityLimit = parseInt(this.dataset.serviceScheduleProductQuantiteMaxParClient) || 0;

          // Check if we need to validate quantities
          const isWithinSchedulePeriod = currentDate >= initialDate && currentDate <= finalDate;
          const hasQuantityLimit = productQuantityLimit > 0;
          
          if (!isWithinSchedulePeriod) {
            throw new Error('The current date is not within the sales period');
          }

          // Get current variant ID and quantity
          const variantId = formData.get('id');
          const requestedQuantity = Number(formData.get('quantity'));
          
          if (!variantId || isNaN(requestedQuantity) || requestedQuantity <= 0) {
            throw new Error('Invalid form data');
          }

          // Fetch and calculate cart quantities
          let cartData;
          const cartResponse = await fetch(`${routes.cart_url}`, config);
          if (!cartResponse.ok) {
            throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
          }
          cartData = await cartResponse.json();

          // Calculate cart quantities
          const cartQuantities = this.calculateCartQuantities(cartData, variantId);
          
          // Calculate order quantities from the current sales period
          const orderQuantities = this.calculateOrderQuantities(
            schedule.customer_orders,
            variantId,
            initialDate,
            finalDate
          );

          // Calculate total quantities
          const totalQuantity = cartQuantities.total + requestedQuantity + orderQuantities.total;
          const totalCurrentProductQuantity = 
            cartQuantities.currentProduct + 
            requestedQuantity + 
            orderQuantities.currentProduct;

          // Validate total cases limit
          if (totalQuantity > schedule.max_item && schedule.max_item > 0) {
            const message = schedule.Strings.cases_limit_error.replace("[max_item]", schedule.max_item);
            this.showConfirmationModal(message);
            throw new Error(`Cases limit exceeded: ${totalQuantity}/${schedule.max_item}`);
          }

          // Validate product-specific limit
          if (hasQuantityLimit && totalCurrentProductQuantity > productQuantityLimit) {
            const message = schedule.Strings.max_item_error.replace("[max_item]", productQuantityLimit);
            this.showConfirmationModal(message);
            throw new Error(`Product limit exceeded: ${totalCurrentProductQuantity}/${productQuantityLimit}`);
          }

          return true;
        } catch (error) {
          console.error('Error in validateServiceSchedule:', error);
          this.removeLoadingState();
          return false;
        }
      }

      calculateCartQuantities(cartData, variantId) {
        const result = { total: 0, currentProduct: 0 };
        
        if (!cartData || !cartData.items || !Array.isArray(cartData.items)) return result;
        
        result.total = cartData.items.reduce((acc, item) => acc + (item.quantity || 0), 0);
        const currentProduct = cartData.items.find(item => String(item.variant_id) === variantId);
        result.currentProduct = currentProduct && currentProduct.quantity ? currentProduct.quantity : 0;
        
        return result;
      }

      calculateOrderQuantities(orders, variantId, initialDate, finalDate) {
        const result = { total: 0, currentProduct: 0 };
        
        if (!Array.isArray(orders)) return result;
        
        // Helper function to safely parse Shopify date format for Safari compatibility
        const parseShopifyDate = (dateString) => {
          if (!dateString) return null;
          
          // More explicit approach to handle all Shopify date formats
          let isoString = dateString;
          
          // Handle the case where there's a space before timezone with colon
          if (isoString.includes(' -') && isoString.includes(':')) {
            // "2024-08-15T11:07:09 -04:00" -> "2024-08-15T11:07:09-04:00"
            isoString = isoString.replace(' -', '-');
          }
          // Handle the case where there's a space before timezone without colon
          else if (isoString.includes(' -') && !isoString.includes(':')) {
            // "2024-08-15 11:07:09 -0400" -> "2024-08-15T11:07:09-04:00"
            isoString = isoString.replace(' ', 'T').replace(' -', '-').replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
          }
          // Handle the case where there's a space between date and time
          else if (isoString.includes(' ') && !isoString.includes('T')) {
            // "2024-08-15 11:07:09-0400" -> "2024-08-15T11:07:09-04:00"
            isoString = isoString.replace(' ', 'T').replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
          }
          
          return new Date(isoString);
        };
        
        for (const order of orders) {
          const createdAt = parseShopifyDate(order.created_at);
          
          if (order.financial_status === 'refunded' || 
              !createdAt || 
              createdAt < initialDate || 
              createdAt > finalDate) {
            continue;
          }

          for (const item of order.items || []) {
            const quantity = item.quantity || 0;
            result.total += quantity;
            
            if (item.variant_id === Number(variantId)) {
              result.currentProduct += quantity;
            }
          }
        }
        
        return result;
      }

      showConfirmationModal(message) {
        // Use the global confirmation modal function
        if (typeof window.showConfirmationModal === 'function') {
          window.showConfirmationModal(message);
        } else {
          // Fallback to alert if modal function is not available
          alert(message);
        }
      }

      removeLoadingState() {
        this.submitButton.classList.remove('loading');
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      get quantityInput() {
        return this.form.querySelector('[name=quantity]');
      }

      resetQuantityToDefault() {
        if (this.quantityInput) {
          const minValue = this.quantityInput.getAttribute('data-min') || 1;
          this.quantityInput.value = minValue;
        }
      }
    }
  );
}
